/**
 * DEV-35 lead-activity date helpers.
 * Business dates are calendar literals (YYYY-MM-DD) handled as strings —
 * never parsed through local-time Date construction, so they cannot shift
 * across browser timezones. Creation timestamps are UTC instants.
 */

const INPUT_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const daysInMonth = (year: number, month: number): number => {
  const lengths = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return lengths[month - 1] ?? 0;
};

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

/**
 * Formats a YYYY-MM-DD (or ISO string whose date prefix is YYYY-MM-DD) as
 * dd.MM.yyyy purely by string slicing — no Date parsing.
 */
export const formatDdMmYyyy = (value: string): string => {
  const datePart = (value || '').split('T')[0];
  const match = /^(\d{1,4})-(\d{1,2})-(\d{1,2})/.exec(datePart);
  if (!match) return '';
  const [, year, month, day] = match;
  return `${pad(Number(day))}.${pad(Number(month))}.${pad(Number(year), 4)}`;
};

/**
 * Strict Gregorian validator for YYYY-MM-DD input values covering the full
 * 0001..9999 supported range. Implemented arithmetically so it cannot inherit
 * the JavaScript Date.UTC year 0–99 coercion to 19xx.
 */
export const isValidGregorianInputDate = (value: string): boolean => {
  const match = INPUT_DATE_PATTERN.exec(value || '');
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || year > 9999) return false;
  if (month < 1 || month > 12) return false;
  return day >= 1 && day <= daysInMonth(year, month);
};

const ISO_INSTANT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d+)?Z$/;

/**
 * Returns the UTC calendar day (dd.MM.yyyy) of a strict ...Z instant, or null
 * when the value is missing or not an unambiguous UTC timestamp. The literal
 * date prefix is validated against the Gregorian calendar and sliced — never
 * normalized through Date.parse, which silently rolls impossible dates like
 * 2026-02-30 into another day.
 */
export const utcDayFromIsoInstant = (value: string | null | undefined): string | null => {
  const match = value ? ISO_INSTANT_PATTERN.exec(value) : null;
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  const y = Number(year), mo = Number(month), d = Number(day);
  if (y < 1 || mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo)) return null;
  if (Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59) return null;
  return `${pad(d)}.${pad(mo)}.${pad(y, 4)}`;
};
